import { Component, computed, Input, input, output } from '@angular/core';
import { BehaviorSubject, timer } from 'rxjs';
import { debounce, distinctUntilChanged, publishReplay, refCount } from 'rxjs/operators';
import { FadeInOut } from '../../../../animations/fade-in-out.animation';
import { AccountStatus } from '../../models/accounts.model';
import type { AutoPayEnrollmentDisplay } from '../../../accounts-notifications/models/accounts-notifications.model';

const statusMapping: Record<
  string,
  {
    text: string;
    class: string;
  }
> = {
  open: {
    text: 'Active',
    class: 'account-status__status active',
  },
  'paid in full': {
    text: 'Paid In Full',
    class: 'account-status__status paid-in-full',
  },
  'past due': {
    text: 'Past Due',
    class: 'account-status__status past-due',
  },
  closed: {
    text: 'Closed',
    class: 'account-status__status closed',
  },
};

@Component({
  selector: 'app-account-item',
  templateUrl: './account-item.component.html',
  styleUrls: ['./account-item.component.scss'],
  animations: [FadeInOut(200, 150)],
  standalone: false,
})
export class AccountItemComponent {
  readonly accountTitle = input.required<string>();
  readonly accountStatus = input.required<AccountStatus>();
  readonly autoPayEnrollment = input<AutoPayEnrollmentDisplay | undefined>(undefined);
  readonly showAutoPay = input.required<boolean>();
  @Input() testId?: string;

  readonly onTitleClick = output<void>();

  readonly statusText = computed(() => {
    const mappedStatus = statusMapping[this.accountStatus()?.toLowerCase() ?? ''];
    return mappedStatus?.text ?? '';
  });
  readonly statusClass = computed(() => {
    const mappedStatus = statusMapping[this.accountStatus()?.toLowerCase() ?? ''];
    return mappedStatus?.class ?? 'account-status__status';
  });
  readonly autopayStatusClass = computed(() => {
    const enrollment = this.autoPayEnrollment();
    if (!enrollment) {
      return 'autopay__status';
    }
    switch (enrollment.kind) {
      case 'enrolled':
        return 'autopay__status autopay--on';
      case 'notEnrolled':
        return 'autopay__status autopay--off';
      case 'skipped':
        return 'autopay__status autopay--skipped';
    }
  });

  isShowAutoPayTooltipFocused = false;

  private readonly _isShowAutoPayTooltipHovered = new BehaviorSubject(false);
  isShowAutoPayTooltipHovered = this._isShowAutoPayTooltipHovered.asObservable().pipe(
    debounce((v) => (!v ? timer(500) : timer(0))),
    distinctUntilChanged(),
    publishReplay(1),
    refCount()
  );

  setIsShowAutoPayTooltipFocused(bool: boolean) {
    this.isShowAutoPayTooltipFocused = bool;
  }

  setIsShowAutoPayTooltipHovered(bool: boolean) {
    this._isShowAutoPayTooltipHovered.next(bool);
  }
}
